"use client";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "next/navigation";
import { apiFetch } from "@/utils/api";
import LoadingState from "@/components/ui/LoadingState";
import ErrorState from "@/components/ui/ErrorState";
import EmptyState from "@/components/ui/EmptyState";
import StoreCard from "@/components/ui/StoreCard";
// import BankWalletOfferCard from "@/components/BankWalletOfferCard";
import Image from "next/image";
import Carousel from "@/components/ui/Carousel";
import Link from "next/link";
import { CheckCircleIcon, ExternalLinkIcon, CopyIcon, ChevronLeftIcon, ChevronRightIcon } from "@/utils/icons";
import { getSafeImageUrl } from "@/utils/images";

// --- INTERFACES  ---
interface Category {
  _id?: string;
  name: string;
  slug: string;
  icon?: string;
  description?: string;
}

interface Offer {
  _id: string;
  title: string;
  store: string;
  storeIcon?: string;
  couponCode: string;
  validTill: string;
  isActive: boolean;
  description?: string;
  discountTypeFlat: string;
  discount: string;
  discountTypeUnit: string;
  oldValue: string;
  oldValueUnit: "price" | "percentage";
  rating?: number;
  badge?: string;
  slug?: string;
  allowRedirectUrl?: boolean;
  redirectUrl?: string;
  usedBy?: number;
  discountType?: 'code' | 'general';
}

interface Store {
  _id?: string;
  name: string;
  slug: string;
  icon?: string;
  description?: string;
  website?: string;
  category?: string;
  isActive?: boolean;
  offersCount?: number;
  coupons?: number;
}

interface BankWalletOffer {
  _id?: string;
  bankName: string;
  title: string;
  description?: string;
  discount?: string;
  maxDiscount?: string;
  validTill?: string;
  color?: string;
  icon?: string;
  validOn?: string;
  offerType: string;
  slug?: string;
}

// --- Breadcrumb Component ---
const Breadcrumb = ({ categoryName }: { categoryName: string }) => (
  <nav className="md:block hidden mb-4" aria-label="Breadcrumb">
    <ol className="flex items-center space-x-1 text-xs text-white">
      <li className="flex items-center">
        <Link
          href="/"
          className="hover:text-white transition-colors capitalize text-primary-200"
        >
          Home
        </Link>
        <svg className="flex-shrink-0 w-3 h-3 ml-2 text-primary-300" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"></path>
        </svg>
      </li>
      <li className="flex items-center">
        <Link
          href="/category"
          className="hover:text-white transition-colors capitalize text-primary-200"
        >
          Categories
        </Link>
        <svg className="flex-shrink-0 w-3 h-3 ml-2 text-primary-300" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"></path>
        </svg>
      </li>
      <li className="flex items-center">
        <span className="break-all max-w-[90px] text-primary-200">
          {categoryName}
        </span>
      </li>
    </ol>
  </nav>
);

// --- New Offer Card Component (Single Column Style) ---
const CategoryOfferCard = ({ offer, copied, setCopied, openTermsId }: {
  offer: Offer;
  copied: string | null;
  setCopied: React.Dispatch<React.SetStateAction<string | null>>;
  toggleTerms: (id: string) => void;
  openTermsId: string | null;
}) => {

  // Helper function for coupon copy
  const handleCopy = async (code: string) => {
    let success = false;
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(code);
        success = true;
      } catch { /* ignored */ }
    }
    if (!success) {
      const textarea = document.createElement("textarea");
      textarea.value = code;
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand("copy");
        success = true;
      } catch { /* ignored */ }
      document.body.removeChild(textarea);
    }
    if (success) {
      setCopied(code);
      setTimeout(() => setCopied(null), 1200);
    }
  };

  /**
   * **COUPON CODE ACTION (Special Logic)**
   * 1. Copy code.
   * 2. Open Offer Detail Page in NEW Tab.
   * 3. Open Coupon Loader in CURRENT Tab (which then redirects to the store).
   */
  const handleCouponActionClick = (offer: Offer) => {
    if (offer.couponCode) {
      handleCopy(offer.couponCode);
    }
    const internalOfferUrl = `/offers/${offer.slug}`;
    const couponLoaderUrl = `/coupon-loader?slug=${offer.slug}`;

    window.open(internalOfferUrl, "_blank", "noopener,noreferrer");
    window.location.href = couponLoaderUrl;
  };

  const handleGeneralActionClick = (offer: Offer) => {
    if (offer.couponCode) {
      handleCopy(offer.couponCode);
    }
    const internalOfferUrl = `/offers/${offer.slug}`;
    const couponLoaderUrl = `/coupon-loader?slug=${offer.slug}`;

    window.open(couponLoaderUrl, "_blank", "noopener,noreferrer");

    window.location.href = internalOfferUrl;
  };

  return (
    <div
      key={offer._id}
      className="bg-white border border-gray-200 shadow-sm rounded-xl overflow-hidden hover:shadow-md transition-shadow duration-200"
    >
      <div
        className="p-4 pt-3 flex flex-col sm:flex-row sm:gap-4 items-start"
         onClick={() => {
          if (window.innerWidth < 640) {
            if (offer.couponCode && offer.discountType === "code") {
              handleCouponActionClick(offer);
            } else {
              handleGeneralActionClick(offer);
            }
          }
        }}
      >
        {/* Offer Content */}
        <div className="w-full sm:w-8/12 space-y-1 cursor-pointer sm:cursor-default lg:p-3">
          <div className="flex items-center space-x-2 mb-1">
            <Image
              src={getSafeImageUrl(offer.storeIcon) || "/assets/storeiconpng.png"}
              alt={offer.store}
              width={24}
              height={24}
              className="w-6 h-6 object-contain rounded-full shadow-sm"
            />
            <span className="text-sm font-medium text-gray-600">{offer.store}</span>
          </div>
          <h2 className="text-md lg:text-xl md:text-xl font-bold lg:line-clamp-1 text-gray-900">
            {offer.title}
          </h2>
          <p className="text-xs text-gray-600 flex justify-between items-center">
            {offer.description}
          </p>
        </div>

        {/* Action Button (Desktop Only) */}
        <div className="w-full sm:w-4/12 flex flex-col items-end pt-3 sm:pt-0 lg:p-3">
          <div className="hidden sm:flex w-full">

            {/* 1. GET DEAL button (for general offers) */}
            {(!offer.couponCode || offer.discountType === "general") && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleGeneralActionClick(offer); // STANDARD ACTION
                }}
                className={`flex-1 cursor-pointer bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2.5 px-3 transition-colors text-sm rounded-lg`}
              >
                <span>GET DEAL</span>
              </button>
            )}

            {/* 2. UNLOCK COUPON CODE button (for coupon offers) */}
            {offer.couponCode && offer.discountType === "code" && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCouponActionClick(offer); // SPECIAL COUPON ACTION
                }}
                className={`w-full cursor-pointer bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 shadow-lg flex items-center justify-center gap-2`}
              >
                <CopyIcon className="w-4 h-4" />
                <span>
                  {copied === offer.couponCode
                    ? "COPIED! GO TO STORE"
                    : "UNLOCK COUPON CODE"}
                </span>
                <ExternalLinkIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Footer Section (Responsive) - Updated for Mobile Action in Row */}
      <div className="border-t border-gray-200 p-3 bg-gray-50 flex justify-between items-center text-sm text-gray-600 rounded-b-xl">

        {/* Left Side: Verification Status (Always visible) */}
        <div className="flex items-center space-x-4 text-xs pl-3"> {/* Added pl-3 for mobile padding on the left */}
          <div className="flex items-center gap-1 text-green-600 font-medium">
            <CheckCircleIcon className="w-3 h-3 fill-green-500 text-white" />
            Verified
          </div>
        </div>

        {/* Middle: MOBILE ACTION BUTTON (Only visible on mobile) */}
        <div className="flex-1 px-2 sm:hidden flex justify-center">
          <button
            className={`w-full max-w-[100px] py-1.5 rounded-lg ${
                offer.couponCode && offer.discountType === "code" ? 'bg-orange-500 hover:bg-orange-600' : 'bg-blue-500 hover:bg-blue-600'
            } text-white font-bold cursor-pointer text-xs tracking-wide shadow-md transition-all duration-200 flex items-center justify-center gap-1`}
            onClick={(e) => {
              e.stopPropagation();
              if (offer.couponCode && offer.discountType === "code") {
                handleCouponActionClick(offer);
              } else {
                handleGeneralActionClick(offer);
              }
            }}
          >
            {offer.couponCode && offer.discountType === "code" ? (
              <>
                <CopyIcon className="w-3 h-3" />
                <span>{copied === offer.couponCode ? "COPIED!" : "GET CODE"}</span>
              </>
            ) : (
              <span>GET DEAL</span>
            )}
          </button>
        </div>


        {/* Right Side: Visit Website (Direct Link) */}
        <div className="flex items-center pr-3"> {/* Added pr-3 for mobile padding on the right */}
          {offer.allowRedirectUrl && offer.redirectUrl && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                // Direct link, bypassing the loader
                window.open(offer.redirectUrl, "_blank", "noopener,noreferrer");
              }}
              className="flex items-center gap-1 text-gray-500 hover:text-blue-600 transition-colors font-medium text-sm"
            >
              <ExternalLinkIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Visit Website</span>
              <span className="sm:hidden text-xs">Website</span>
            </button>
          )}
        </div>
      </div>

      {/* Terms & Conditions Accordion */}
      {offer.description && openTermsId === offer._id && (
        <div className="p-4 pt-2 text-xs text-gray-700 bg-gray-50 border-t border-gray-200">
          <h4 className="font-semibold mb-1">Details:</h4>
          <p>{offer.description}</p>
        </div>
      )}
    </div>
  );
};

// --- Main Page Component (CategorySlugPage) ---
export default function CategorySlugPage() {
  const params = useParams();
  const slug =
    typeof params === "object" && params !== null && "slug" in params
      ? (params as Record<string, string>).slug
      : undefined;
  const [category, setCategory] = useState<Category | null>(null);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [bankWalletOffers, setBankWalletOffers] = useState<BankWalletOffer[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastVerified, setLastVerified] = useState<string>("");
  const [copied, setCopied] = useState<string | null>(null);
  const [openTermsId, setOpenTermsId] = useState<string | null>(null);
  const trendingCategoriesRef = useRef<HTMLDivElement>(null);


  const toggleTerms = (id: string) => {
    setOpenTermsId((current) => (current === id ? null : id));
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    setCategory(null);
    setOffers([]);
    setStores([]);
    setBankWalletOffers([]);
    if (!slug) return;

    // Simplified fetch logic for demonstration
    apiFetch<{ success: boolean; data: Category }>(`/categories/slug/${slug}`)
      .then((res) => {
        setCategory(res.data);
        return Promise.all([
          apiFetch<{ success: boolean; data: Offer[] }>(`/categories/slug/${slug}/offers`),
          apiFetch<{ success: boolean; data: Store[] }>(`/categories/slug/${slug}/stores-with-offer-count`),
          apiFetch<{ success: boolean; data: BankWalletOffer[] }>(`/categories/slug/${slug}/bank-wallet-offers`),
        ]);
      })
      .then(([offersRes, storesRes, bankWalletRes]) => {
        const enhancedOffers = (Array.isArray(offersRes.data) ? offersRes.data : []).map(o => ({
          ...o,
          // Correctly determines discountType
          discountType: o.couponCode && o.couponCode.length > 0 ? 'code' : 'general',
          usedBy: Math.floor(Math.random() * 500)
        })) as Offer[];

        setOffers(enhancedOffers);
        setStores(Array.isArray(storesRes.data) ? storesRes.data : []);
        setBankWalletOffers(Array.isArray(bankWalletRes.data) ? bankWalletRes.data : []);
        setLastVerified(
          new Date().toLocaleDateString("en-IN", {
            weekday: "short",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })
        );
      })
      .catch((err: unknown) => {
        setError((err as Error).message || "Failed to load category");
      })
      .finally(() => setLoading(false));

  }, [slug]);

  const scrollTrendingCategories = (direction: 'left' | 'right') => {
    if (trendingCategoriesRef.current) {
      const scrollContainer = trendingCategoriesRef.current;
      const isMobile = window.innerWidth < 640;

      let scrollAmount: number;

      if (isMobile) {
        const cardWidth = 150;
        const gap = 16;
        scrollAmount = (2 * cardWidth) + (2 * gap);
      } else {
        scrollAmount = 300;
      }

      const currentScroll = scrollContainer.scrollLeft;

      if (direction === 'left') {
        scrollContainer.scroll({
          left: currentScroll - scrollAmount,
          behavior: 'smooth'
        });
      } else {
        scrollContainer.scroll({
          left: currentScroll + scrollAmount,
          behavior: 'smooth'
        });
      }
    }
  };

  if (loading) return <LoadingState message="Loading category..." />;
  if (error)
    return (
      <ErrorState message={error} onRetry={() => window.location.reload()} />
    );
  if (!category) return <EmptyState message="Category not found." emoji="❓" />;

  const totalDeals =
    Number(offers.length) +
    Number(stores.length) +
    Number(bankWalletOffers.length);

  return (
    <div className="bg-gray-50 pt-0 lg:pb-3 min-h-screen">
      {/* Hero Section: Gradient Banner (Desktop Only) */}
      <div className="md:block hidden bg-gradient-to-r from-primary-600 to-purple-700 text-white relative h-88 sm:h-72 md:h-44">
      </div>

      {/* Main Content Container*/}
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-6 lg:p-8 py-4 px-2 md:-mt-44 relative z-10">

        {/* Sidebar */}
        <aside className="w-full md:w-64 flex-shrink-0">

          {/* Breadcrumb for Desktop */}
          <Breadcrumb categoryName={category.name} />

          {/* DESKTOP SIDEBAR STATS CARD */}
          <div className="hidden md:flex flex-col w-full bg-white rounded-xl shadow-lg p-6 mb-4 border border-primary-100 items-start justify-start overflow-hidden">
            <div className="flex flex-col items-center w-full mb-4">
              <Image
                src={
                  getSafeImageUrl(category.icon) || "/assets/OfferCodeFlight.jpg"
                }
                alt={category.name}
                width={80}
                height={80}
                className="object-contain w-20 h-20 rounded-xl shadow-md mb-2"
              />
              <div className="text-xl font-bold text-center text-gray-900 mt-2">
                {category.name}
              </div>
            </div>
            <div className="w-full text-center py-3 border-y border-gray-200 mb-4">
              <div className="text-sm font-medium text-gray-500">Total Deals</div>
              <div className="text-4xl font-extrabold text-primary-600">
                {totalDeals}
              </div>
            </div>
            <div className="w-full text-sm text-gray-700 space-y-2">
              <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
                <span className="font-medium text-gray-600">Coupon Offers</span>
                <span className="font-bold text-primary-600">
                  {Number(offers.length)}
                </span>
              </div>
              <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
                <span className="font-medium text-gray-600">Store Offers</span>
                <span className="font-bold text-cyan-600">
                  {Number(stores.length)}
                </span>
              </div>
              <div className="flex justify-between items-center py-1.5">
                <span className="font-medium text-gray-600">Bank/Wallet Offers</span>
                <span className="font-bold text-fuchsia-600">
                  {Number(bankWalletOffers.length)}
                </span>
              </div>
            </div>
            <div className="w-full text-center mt-5 pt-3 border-t border-gray-100">
              <div className="text-xs flex items-center justify-center gap-1 text-green-600 font-medium">
                <CheckCircleIcon className="w-4 h-4" /> Verified On:{" "}
                <span className="text-gray-500">{lastVerified}</span>
              </div>
            </div>
          </div>

          {/* MOBILE STATS CARD */}
          <div className="md:hidden w-full bg-primary-200 rounded-xl shadow-lg p-4 border border-primary-100 flex flex-col overflow-hidden mt-2">
            <div className="flex items-start justify-between pb-3 border-b border-white">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-12 h-12 bg-gray-50 rounded-xl p-1 border border-white shadow-sm">
                  <Image
                    src={
                      getSafeImageUrl(category.icon) || "/assets/OfferCodeFlight.jpg"
                    }
                    alt={category.name}
                    width={48}
                    height={48}
                    className="object-contain w-full h-full rounded-lg"
                  />
                </div>
                <div className="flex flex-col pt-0.5">
                  <div className="text-sm text-gray-500 font-medium leading-none">Category</div>
                  <div className="text-xl font-bold text-gray-900 leading-tight">
                    {category.name}
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end pt-0.5">
                <div className="text-xs text-gray-500 font-medium flex items-center gap-1">
                  <CheckCircleIcon className="text-green-500 w-3 h-3" /> Verified
                </div>
                <div className="text-sm font-semibold text-gray-600">
                  {lastVerified}
                </div>
              </div>
            </div>
            <div className="flex justify-start items-center my-3">
              <div className="flex flex-col">
                <div className="text-sm text-gray-700 font-medium">Total Deals Available:</div>
                <div className="text-2xl font-bold text-primary-600 leading-tight">
                  {totalDeals}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-100">
              <div className="flex flex-col items-center bg-primary-50 rounded-lg py-2 px-1 text-center">
                <div className="text-xl font-bold text-primary-600">
                  {Number(offers.length)}
                </div>
                <div className="text-[10px] font-semibold text-primary-700 uppercase leading-tight mt-1">
                  {Number(offers.length) > 1 ? "Coupons" : "Coupon"}
                </div>
              </div>
              <div className="flex flex-col items-center bg-cyan-50 rounded-lg py-2 px-1 text-center">
                <div className="text-xl font-bold text-cyan-600">
                  {Number(stores.length)}
                </div>
                <div className="text-[10px] font-semibold text-cyan-700 uppercase leading-tight mt-1">
                  {Number(stores.length) > 1 ? "Stores" : "Store"}
                </div>
              </div>
              <div className="flex flex-col items-center bg-fuchsia-50 rounded-lg py-2 px-1 text-center">
                <div className="text-xl font-bold text-fuchsia-600">
                  {Number(bankWalletOffers.length)}
                </div>
                <div className="text-[10px] font-semibold text-fuchsia-700 uppercase leading-tight mt-1">
                  {Number(bankWalletOffers.length) > 1
                    ? "Banks"
                    : "Bank"}
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Sidebar Navigation/Ad */}
          <aside className="hidden md:block w-full md:w-64 flex-shrink-0 bg-white rounded-xl shadow-lg p-6 mb-6 md:mb-0 border border-primary-100">
            <nav className="flex flex-col gap-3 text-sm">
              <Link className="hover:underline text-gray-700 hover:text-primary-600 font-medium" href="/category">
                See All Categories
              </Link>
              <Link className="hover:underline text-gray-700 hover:text-primary-600 font-medium" href="/store">
                See All Stores
              </Link>
              <Link className="hover:underline text-gray-700 hover:text-primary-600 font-medium" href="/offers">
                See All Offers
              </Link>
              {/* <Link className="hover:underline text-gray-700 hover:text-primary-600 font-medium" href="/cashback">
                See All CashBack
              </Link>
              <Link className="hover:underline text-gray-700 hover:text-primary-600 font-medium" href="/bank-wallet-offers">
                See All Banks
              </Link> */}
            </nav>
            <div className="mt-10 mb-6 w-full flex justify-center">
              <div className="w-full">
                <Carousel orientation="vertical" />
              </div>
            </div>
          </aside>
        </aside>

        {/* Main Content Area */}
        <main className="w-full flex-1">
          {/* Main Header (Title and Description) */}
          <div className="md:block hidden flex flex-col gap-1 mb-6 py-4 md:py-0 text-white md:text-gray-900">
            <div className="flex items-center gap-4">
              {category.icon && category.icon.length > 4 && (
                <div className="hidden md:flex w-14 h-14 mt-5 items-center justify-center flex-shrink-0 bg-white rounded-xl border border-gray-100 shadow-lg">
                  <Image
                    src={category.icon}
                    alt={category.name}
                    width={56}
                    height={56}
                    className="object-contain max-h-12"
                  />
                </div>
              )}
              <h1 className="lg:text-4xl text-2xl font-extrabold md:font-bold text-white">
                {category.name} Coupons & Discounts
              </h1>
            </div>

            {category.description && (
              <div className="text-white/80 text-base sm:mt-2 lg:mt-0 md:ml-18">
                {category.description}
              </div>
            )}
          </div>

          {/* Stores Section - Horizontal Scroll */}
          <div className="p-4 md:p-6  md:pt-14 lg:pt-13 pt-2">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-bold mb-4 text-gray-800">
                Stores in {category.name}
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={() => scrollTrendingCategories('left')}
                  className="p-1 bg-gray-100 rounded-full shadow-md text-gray-600 hover:bg-gray-200 transition-colors"
                  aria-label="Scroll Stores Left"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => scrollTrendingCategories('right')}
                  className="p-1 bg-gray-100 rounded-full shadow-md text-gray-600 hover:bg-gray-200 transition-colors"
                  aria-label="Scroll Stores Right"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            {stores.length === 0 ? (
              <EmptyState message="No stores found for this category." emoji="🏬" />
            ) : (
              <div
                className="overflow-x-auto pb-2 md:w-[55vw] sm:w-[70vw] lg:w-[65vw]"
                ref={trendingCategoriesRef}
                style={{ scrollBehavior: "smooth" }}
              >
                <div className="flex gap-4 w-max">
                  {stores.map((store, i) => (
                    <div
                      key={store._id || i}
                      className="flex-shrink-0 w-[150px] sm:w-[180px]"
                    >
                      <StoreCard
                        name={store.name}
                        logo={store.icon || "/vercel.svg"}
                        offers={Number(store.offersCount) - Number(store.coupons) || 0}
                        coupons={store.coupons || 0}
                        slug={store.slug}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Offers Section - New Single-Column Card Style */}
          <div className="p-4 md:p-6 mb-6">
            <h2 className="text-xl font-bold mb-4 text-gray-800">
              Offers in {category.name}
            </h2>
            {offers.length === 0 ? (
              <EmptyState message="No coupon offers found for this category." emoji="🎁" />
            ) : (
              <div className=" md:w-[55vw] sm:w-[70vw] lg:w-[65vw] space-y-4">
                {offers.map((offer) => (
                  <CategoryOfferCard
                    key={offer._id}
                    offer={offer}
                    copied={copied}
                    setCopied={setCopied}
                    toggleTerms={toggleTerms}
                    openTermsId={openTermsId}
                  />
                ))}
              </div>
            )}
          </div>


          {/* Bank/Wallet Offers Section - Horizontal Scroll */}
          {/* <div className="p-2 md:px-6">
            <h2 className="text-xl font-bold mb-4 text-gray-800">
              Bank/Wallet Offers in {category.name}
            </h2>
            {bankWalletOffers.length === 0 ? (
              <EmptyState
                message="No bank/wallet offers found for this category."
                emoji="💳"
              />
            ) : (
              <div className="overflow-x-auto pb-2">
                <div className="flex gap-4 w-max">
                  {bankWalletOffers.map((offer, i) => (
                    <div key={offer._id || i} className="min-w-[280px] sm:min-w-[320px]">
                      <BankWalletOfferCard offer={offer} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div> */}
        </main>
      </div>
    </div>
  );
}